import { useState } from 'react'
import { Box, makeStyles, styled } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import {
    ClaimControl,
    DelegateMenuButton,
    DelegationsHistory,
    ItemDetails,
    ProcessSteps,
} from 'components'
import Overview from './Overview'
import RequestItemApprovers from './RequestItemApprovers'
import RequestItemComments from './RequestItemComments'
import useSubcomponents from 'useSubcomponents'
import {
    useApplicationSettings,
    useCancelRequestItem,
    useCurrentPerson,
    useRefreshRequestItemRisks,
    useRequestItemApprovalSteps,
    useRequestItemDelegationsHistory,
    useRequestItemRisks,
} from 'hooks'
import { isNilOrEmpty } from 'packages/core'
import { smallScreenWidth } from 'utils'
import { RiskIndicator } from 'packages/eid-ui'
import { CancelationButton } from 'components/Button/CancelationButton'
import { WorkflowDecisionModal } from 'components/WorkflowDecisionModal'
import { ItemApprovalDecisionPrompt } from './ItemApprovalDecisionPrompt'
import { RefreshButton } from 'components/Button/RefreshButton'
import { RisksWithViolations } from 'components/V2/RisksWithViolations'
import { PendingToDoList } from './PendingToDoList'

const TabsContainerBox = styled(Box)({
    backgroundColor: '#fbfbfd',
    borderBottom: '1px solid #d8dadd',

    paddingRight: '30px',
    display: 'flex',
    justifyContent: 'space-between',

    '& .Tabs-root': {
        display: 'flex',
    },

    [`@media (max-width:${smallScreenWidth})`]: {
        flexDirection: 'column-reverse',
        paddingTop: '20px',
        paddingLeft: '20px',
        paddingRight: '20px',

        '& .Tabs-root': {
            marginLeft: '-20px',
        },
    },
})

const OverflowContainer = styled(Box)({
    [`@media (max-width:${smallScreenWidth})`]: {
        overflowX: 'auto',
    },
})

const useStyles = makeStyles({
    subHeader: {
        position: 'sticky',
        top: '0px',
        zIndex: 200,
    },
    subHeaderContent: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        padding: '7px 20px',
        display: 'flex',
        alignItems: 'center',
    },
    riskIndicator: {
        backgroundColor: '#fdf1f3',
        opacity: 1,
        fontWeight: 'bold',
    },
})

const BusinessRequestItemDetails = ({ data, refetch }) => {
    const { t } = useTranslation()
    const classes = useStyles()

    const { data: appConfig } = useApplicationSettings()

    const { data: currentPerson } = useCurrentPerson()

    const [activeTab, setActiveTab] = useState('overview')

    const {
        hasAccessToDelegationsHistoryControl,
        hasAccessToRequestItemProcessStepsControl,
        hasAccessToRequestItemCurrentStepApproversControl,
        hasAccessToRequestItemClaimControl,
        hasAccessToRequestItemDelegateControl,
        hasAccessToAdminCancelRequestItemControl,
        hasAccessToCancelRequestItemControl,
        hasAccessToRefreshViolationsControl,
        canSeeRiskViolationsByRisks,
    } = useSubcomponents()

    const isClaimedOrAssigned = !isNilOrEmpty(data.claimedApproverId)

    const isClaimedOrClaimable =
        data.currentApprovalStepId && isNilOrEmpty(data.assignedByPersonId)

    const isItemNotActionable =
        data.businessRequestStatus === 'Expired' ||
        data.businessRequestStatus === 'Canceled' ||
        data.status === 'Canceled'

    const isDelegatable = data.currentApprovalStepId

    const [
        cancelRequestItem,
        { isLoading: isCanceling },
    ] = useCancelRequestItem(data.id)

    const [
        refreshRequestItemRisks,
        { isLoading: isRefreshing },
    ] = useRefreshRequestItemRisks(data.businessRequestId, data.id)

    return (
        <>
            <Box
                className={classes.subHeader}
                style={{
                    backgroundImage: appConfig.style.colors.primary?.gradient,
                    backgroundColor: appConfig.style.colors.primary?.header,
                }}
            >
                <Box className={classes.subHeaderContent}>
                    {hasAccessToRefreshViolationsControl && (
                        <RefreshButton
                            onClick={refreshRequestItemRisks}
                            isRefreshing={isRefreshing}
                        />
                    )}

                    {data.riskLevel && (
                        <Box marginLeft={'15px'}>
                            <RiskIndicator
                                variant={data.riskLevel}
                                label={t(`MyTasks_${data.riskLevel}Risk`)}
                                className={classes.riskIndicator}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            <TabsContainerBox>
                <Box marginLeft="-13px" className="Tabs-root">
                    <ItemDetails.Tabs
                        variant="scrollable"
                        scrollButtons="on"
                        value={activeTab}
                        onChange={(_, value) => setActiveTab(value)}
                    >
                        <ItemDetails.Tabs.Tab
                            value="overview"
                            label={t('MyTasks_Overview')}
                        />
                        {hasAccessToRequestItemProcessStepsControl && (
                            <ItemDetails.Tabs.Tab
                                value="processSteps"
                                label={t('MyTasks_ProcessSteps')}
                            />
                        )}
                        {hasAccessToRequestItemCurrentStepApproversControl &&
                            !isClaimedOrAssigned &&
                            data.approverCount !== null &&
                            data.currentApprovalStepId && (
                                <ItemDetails.Tabs.Tab
                                    value="currentStepApprovers"
                                    label={t('MyTasks_CurrentStepApprovers')}
                                />
                            )}
                        {hasAccessToDelegationsHistoryControl && (
                            <ItemDetails.Tabs.Tab
                                value="delegationsHistory"
                                label={t('MyTasks_Delegations')}
                            />
                        )}
                        {data.riskCount > 0 && canSeeRiskViolationsByRisks && (
                            <ItemDetails.Tabs.Tab
                                value={'violations'}
                                label={t('MyTasks_RiskViolations')}
                            />
                        )}
                    </ItemDetails.Tabs>
                </Box>
                <Box display="flex" alignItems="center">
                    {hasAccessToRequestItemClaimControl &&
                        isClaimedOrClaimable &&
                        data.allowAssignApprover && (
                            <Box marginRight="8px">
                                <ClaimControl
                                    disabled={isItemNotActionable}
                                    page="RequestItems"
                                    isApprover={data.isApprover}
                                    requestId={data.businessRequestId}
                                    requestItemId={data.id}
                                    currentApprovalStepId={
                                        data.currentApprovalStepId
                                    }
                                    claimedApproverId={data.claimedApproverId}
                                    claimedApproverFriendlyName={
                                        data.claimedApproverFriendlyName
                                    }
                                />
                            </Box>
                        )}
                    {hasAccessToRequestItemDelegateControl && isDelegatable && (
                        <DelegateMenuButton
                            page="RequestItems"
                            disabled={isItemNotActionable}
                            requestId={data.businessRequestId}
                            requestItemId={data.id}
                            isApprover={data.isApprover}
                            currentApprovalStepId={data.currentApprovalStepId}
                            assignedByPersonId={data.assignedByPersonId}
                            assignedByPersonFriendlyName={
                                data.assignedByPersonFriendlyName
                            }
                            claimedApproverId={data.claimedApproverId}
                            claimedApproverFriendlyName={
                                data.claimedApproverFriendlyName
                            }
                            allowAddApprover={data.allowAddApprover}
                            allowAssignApprover={data.allowAssignApprover}
                        />
                    )}
                </Box>
            </TabsContainerBox>

            {activeTab === 'overview' && <Overview data={data} />}
            {activeTab === 'processSteps' && (
                <OverflowContainer>
                    <ProcessSteps
                        useDataHook={useRequestItemApprovalSteps}
                        requestId={data.businessRequestId}
                        itemId={data.id}
                        isCanceled={data.businessRequestStatus === 'Canceled'}
                    />
                </OverflowContainer>
            )}
            {activeTab === 'currentStepApprovers' && (
                <RequestItemApprovers
                    id={data.id}
                    totalCount={data.approverCount}
                    stepId={data.currentApprovalStepId}
                />
            )}
            {activeTab === 'delegationsHistory' && (
                <OverflowContainer>
                    <DelegationsHistory
                        useDataHook={useRequestItemDelegationsHistory}
                        requestId={data.businessRequestId}
                        page="RequestItem"
                        itemId={data.id}
                    />
                </OverflowContainer>
            )}
            {activeTab === 'violations' && (
                <RisksWithViolations
                    page={'RequestItems'}
                    requestId={data.businessRequestId}
                    itemId={data.id}
                    requestIdOrItemId={data.id}
                    useDataHook={useRequestItemRisks}
                />
            )}

            <PendingToDoList
                itemId={data.id}
                afterwardsChildren={
                    data &&
                    !isItemNotActionable &&
                    data.isApprovable &&
                    (data.decisionWorkflowName ? (
                        <Box
                            marginY={'24px'}
                            marginX={'28px'}
                            display={'flex'}
                            justifyContent={'flex-end'}
                        >
                            <WorkflowDecisionModal
                                page={'RequestItems'}
                                requestId={data.businessRequestId}
                                itemId={data.id}
                                currentApprovalStepId={
                                    data.currentApprovalStepId
                                }
                                decisionWorkflowName={data.decisionWorkflowName}
                            />
                        </Box>
                    ) : (
                        <ItemApprovalDecisionPrompt
                            requestItem={data}
                            refetch={refetch}
                        />
                    ))
                }
            />

            <Box marginY="18px">
                <RequestItemComments
                    requestId={data.businessRequestId}
                    itemId={data.id}
                />
            </Box>

            {data.isCancellable &&
                (hasAccessToAdminCancelRequestItemControl ||
                    (hasAccessToCancelRequestItemControl &&
                        data.initiatorPersonId === currentPerson.id)) && (
                    <Box marginY={'32px'} marginX={'32px'}>
                        <CancelationButton
                            confirmationMessage={t(
                                'MyTasks_RequestItemCancelationConfirmation',
                            )}
                            isCanceling={isCanceling}
                            onClick={cancelRequestItem}
                        >
                            {t('MyTasks_CancelRequestItem')}
                        </CancelationButton>
                    </Box>
                )}
        </>
    )
}

export default BusinessRequestItemDetails
